import { dynamicConfigStringListIncludes } from '@atlaskit/tmp-editor-statsig/dynamic-config-value-contains';

/**
 * Statsig dynamic config for Milestone 1 streaming UX optional slices.
 *
 * The config holds a string list under its `value` key; a feature is enabled when
 * its token is present. Review-moment tokens are prefixed so this config can later
 * hold other M1 slices without collisions.
 *
 * Review-moment tokens:
 *   - `'review-moment-show-diff-by-default'`
 *   - `'review-moment-step-through'`
 *   - `'review-moment-show-accepted-pill'`
 *   - `'review-moment-diff-based-segmenting'`
 *
 * Semantics: an absent token (or unset / not-ready config) means the feature is
 * OFF. Rollout is opt-in via the config's `value` list. [CCI-19338]
 */
export const STREAMING_UX_M1_CONFIG = 'platform_editor_ai_streaming_ux_m1_config';

export const REVIEW_MOMENT_TOKENS = {
	showDiffByDefault: 'review-moment-show-diff-by-default',
	stepThrough: 'review-moment-step-through',
	showAcceptedPill: 'review-moment-show-accepted-pill',
	diffBasedSegmenting: 'review-moment-diff-based-segmenting',
} as const;

/**
 * Whether the Review Moment opens with deletions shown by default.
 */
export function isReviewMomentShowDiffByDefaultEnabled(): boolean {
	return dynamicConfigStringListIncludes(
		STREAMING_UX_M1_CONFIG,
		REVIEW_MOMENT_TOKENS.showDiffByDefault,
	);
}

/**
 * Whether the step-through experience is available in the Review Moment.
 */
export function isReviewMomentStepThroughEnabled(): boolean {
	return dynamicConfigStringListIncludes(STREAMING_UX_M1_CONFIG, REVIEW_MOMENT_TOKENS.stepThrough);
}

/**
 * Whether the Review Moment shows the "Accepted" confirmation pills — the full
 * `AcceptedToast` (after accepting all changes) and the per-segment
 * `MiniAcceptedToast` (while stepping through). Default OFF (absent token = no
 * pills): accepting tears the Review moment down without a confirmation pill. [CCI-19074]
 */
export function isReviewMomentAcceptedPillEnabled(): boolean {
	return dynamicConfigStringListIncludes(
		STREAMING_UX_M1_CONFIG,
		REVIEW_MOMENT_TOKENS.showAcceptedPill,
	);
}

/**
 * Whether Post Stream Review splits a top-level change into finer diff-level
 * sub-segments (`diffSegments`) instead of segmenting at the top-level node
 * boundary only. [CCI-18182]
 */
export function isReviewMomentDiffBasedSegmentingEnabled(): boolean {
	return dynamicConfigStringListIncludes(
		STREAMING_UX_M1_CONFIG,
		REVIEW_MOMENT_TOKENS.diffBasedSegmenting,
	);
}
